// Маршрути (React Router 7, createBrowserRouter). Кожен модуль додає/замінює СВОЇ маршрути лише між своїми маркерами
// // <Uxx> … // </Uxx> у масиві дочірніх маршрутів шелла (публічні /p — між // <U14-public> … // </U14-public>).
// Сторінки модулів — lazy-імпорти з власної теки: { path: 'x', lazy: async () => ({ Component: (await import('@/features/x/XPage')).XPage }) }.
// Статичний сегмент перекриває динамічний: власна сторінка довідника /directories/<code> має пріоритет над /directories/:code.
import { Calendar, ChartColumn, Cpu, ShieldCheck, TriangleAlert, UserRound } from 'lucide-react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { LoginPage } from '@/features/auth/LoginPage';
import { GuestOnly, ProtectedRoute } from '@/lib/auth';
import { LoadingBlock } from '@/ui';
import { AppShell } from './AppShell';
import { HomeRedirect, NotFoundPage, PublicLayout, RouteError } from './pages';
import { SemesterProvider } from './semester';

const shellRoutes: RouteObject[] = [
  { index: true, element: <HomeRedirect /> },
  // <U01>
  {
    path: 'directories',
    lazy: async () => ({ Component: (await import('@/features/directories/DirectoriesIndexPage')).DirectoriesIndexPage }),
  },
  {
    path: 'directories/imports',
    lazy: async () => ({ Component: (await import('@/features/directories/ImportsPage')).ImportsPage }),
  },
  {
    path: 'directories/imports/:jobId',
    lazy: async () => ({ Component: (await import('@/features/directories/ImportsPage')).ImportJobPage }),
  },
  {
    path: 'directories/:code',
    lazy: async () => ({ Component: (await import('@/features/directories/DirectoryPage')).DirectoryRoutePage }),
  },
  {
    path: 'directories/:code/:id',
    lazy: async () => ({ Component: (await import('@/features/directories/DirectoryRecordPage')).DirectoryRecordRoutePage }),
  },
  // </U01>
  // <U02>
  { path: 'structure', lazy: async () => ({ Component: (await import('@/features/structure/StructurePage')).StructurePage }) },
  { path: 'directories/university', lazy: async () => ({ Component: (await import('@/features/structure/UniversityPage')).UniversityPage }) },
  {
    path: 'directories/departments/:id',
    lazy: async () => ({ Component: (await import('@/features/structure/DepartmentCardPage')).DepartmentCardPage }),
  },
  { path: 'directories/curricula/:id', lazy: async () => ({ Component: (await import('@/features/structure/CurriculumPage')).CurriculumPage }) },
  { path: 'directories/subjects/:id', lazy: async () => ({ Component: (await import('@/features/structure/SubjectCardPage')).SubjectCardPage }) },
  {
    path: 'directories/lesson_requirements',
    lazy: async () => ({ Component: (await import('@/features/structure/LessonRequirementsPage')).LessonRequirementsPage }),
  },
  // </U02>
  // <U03>
  {
    path: 'directories/locations',
    lazy: async () => ({ Component: (await import('@/features/resources/LocationsPage')).LocationsPage }),
  },
  {
    path: 'directories/locations/:id',
    lazy: async () => ({ Component: (await import('@/features/resources/LocationCardPage')).LocationCardPage }),
  },
  {
    path: 'directories/rooms/:id',
    lazy: async () => ({ Component: (await import('@/features/resources/RoomCardPage')).RoomCardPage }),
  },
  {
    path: 'directories/travel_matrix',
    lazy: async () => ({ Component: (await import('@/features/resources/TravelMatrixPage')).TravelMatrixPage }),
  },
  // </U03>
  // <U04>
  {
    path: 'directories/groups',
    lazy: async () => ({ Component: (await import('@/features/contingent/GroupsPage')).GroupsPage }),
  },
  {
    path: 'directories/groups/:id',
    lazy: async () => ({ Component: (await import('@/features/contingent/GroupCardPage')).GroupCardPage }),
  },
  {
    path: 'directories/streams/:id',
    lazy: async () => ({ Component: (await import('@/features/contingent/StreamPage')).StreamPage }),
  },
  // </U04>
  // <U05>
  {
    path: 'directories/teachers/:id',
    lazy: async () => ({ Component: (await import('@/features/teachers/TeacherCardPage')).TeacherCardPage }),
  },
  {
    path: 'directories/teachers/:id/availability',
    lazy: async () => ({ Component: (await import('@/features/teachers/TeacherAvailabilityPage')).TeacherAvailabilityPage }),
  },
  {
    path: 'directories/teacher_availability',
    lazy: async () => ({ Component: (await import('@/features/teachers/AvailabilitySummaryPage')).AvailabilitySummaryPage }),
  },
  {
    path: 'my/availability',
    lazy: async () => ({ Component: (await import('@/features/teachers/MyAvailabilityPage')).MyAvailabilityPage }),
  },
  // </U05>
  // <U06>
  { path: 'calendar', lazy: async () => ({ Component: (await import('@/features/calendar/CalendarYearPage')).CalendarYearPage }) },
  { path: 'directories/time_grids', lazy: async () => ({ Component: (await import('@/features/calendar/CalendarDirectoryPages')).TimeGridsDirectoryPage }) },
  { path: 'directories/time_grids/:id', lazy: async () => ({ Component: (await import('@/features/calendar/TimeGridPage')).TimeGridPage }) },
  { path: 'directories/calendar_periods', lazy: async () => ({ Component: (await import('@/features/calendar/CalendarDirectoryPages')).CalendarPeriodsDirectoryPage }) },
  { path: 'directories/holidays', lazy: async () => ({ Component: (await import('@/features/calendar/CalendarDirectoryPages')).HolidaysDirectoryPage }) },
  // </U06>
  // <U07>
  { path: 'workload', lazy: async () => ({ Component: (await import('@/features/workload/WorkloadPage')).WorkloadPage }) },
  { path: 'workload/:id', lazy: async () => ({ Component: (await import('@/features/workload/AssignmentPage')).AssignmentPage }) },
  {
    path: 'directories/teaching_assignments',
    lazy: async () => ({ Component: (await import('@/features/workload/WorkloadPage')).TeachingAssignmentsRedirect }),
  },
  // </U07>
  // <U08>
  {
    path: 'validation',
    // handle.icon — іконка розділу (імпорт ShieldCheck спільний, F)
    handle: { icon: ShieldCheck },
    lazy: async () => ({ Component: (await import('@/features/validation/ValidationPage')).ValidationPage }),
  },
  // </U08>
  // <U09>
  // </U09>
  // <U10>
  {
    path: 'generation',
    handle: { title: 'Генерація', icon: Cpu },
    lazy: async () => ({ Component: (await import('@/features/generation/GenerationRunsPage')).GenerationRunsPage }),
  },
  {
    path: 'generation/new',
    handle: { title: 'Новий запуск генерації', icon: Cpu },
    lazy: async () => ({ Component: (await import('@/features/generation/GenerationWizardPage')).GenerationWizardPage }),
  },
  {
    path: 'generation/:id',
    handle: { title: 'Запуск генерації', icon: Cpu },
    lazy: async () => ({ Component: (await import('@/features/generation/GenerationRunPage')).GenerationRunPage }),
  },
  // </U10>
  // <U11>
  {
    path: 'conflicts',
    handle: { title: 'Конфлікти', icon: TriangleAlert },
    lazy: async () => ({ Component: (await import('@/features/conflicts/ConflictsPage')).ConflictsPage }),
  },
  // </U11>
  // <U12>
  {
    path: 'schedule',
    handle: { title: 'Розклад', icon: Calendar },
    lazy: async () => ({ Component: (await import('@/features/schedule/SchedulePage')).SchedulePage }),
  },
  // </U12>
  // <U13>
  { path: 'versions', lazy: async () => ({ Component: (await import('@/features/versions/VersionsPage')).VersionsPage }) },
  { path: 'versions/compare', lazy: async () => ({ Component: (await import('@/features/versions/ComparePage')).ComparePage }) },
  { path: 'versions/:id', lazy: async () => ({ Component: (await import('@/features/versions/VersionPage')).VersionPage }) },
  { path: 'approvals', lazy: async () => ({ Component: (await import('@/features/versions/ApprovalsPage')).ApprovalsPage }) },
  // </U13>
  // <U14>
  {
    path: 'my',
    handle: { title: 'Мій розклад', icon: UserRound },
    lazy: async () => ({ Component: (await import('@/features/publishing/MyPage')).MyPage }),
  },
  { path: 'views/:kind/:id', lazy: async () => ({ Component: (await import('@/features/publishing/ViewPage')).ViewPage }) },
  // </U14>
  // <U15>
  {
    path: 'analytics',
    handle: { title: 'Аналітика', icon: ChartColumn },
    lazy: async () => ({ Component: (await import('@/features/analytics/AnalyticsPage')).AnalyticsPage }),
  },
  {
    path: 'analytics/:tab',
    handle: { title: 'Аналітика', icon: ChartColumn },
    lazy: async () => ({ Component: (await import('@/features/analytics/AnalyticsPage')).AnalyticsPage }),
  },
  // </U15>
  // <U16>
  { path: 'audit', lazy: async () => ({ Component: (await import('@/features/admin/AuditPage')).AuditPage }) },
  { path: 'notifications', lazy: async () => ({ Component: (await import('@/features/admin/NotificationsPage')).NotificationsPage }) },
  { path: 'admin', lazy: async () => ({ Component: (await import('@/features/admin/AdminIndexRedirect')).AdminIndexRedirect }) },
  { path: 'admin/users', lazy: async () => ({ Component: (await import('@/features/admin/UsersPage')).UsersPage }) },
  { path: 'admin/users/:id', lazy: async () => ({ Component: (await import('@/features/admin/UserPage')).UserPage }) },
  { path: 'admin/rules', lazy: async () => ({ Component: (await import('@/features/admin/RulesPage')).RulesPage }) },
  { path: 'admin/weights', lazy: async () => ({ Component: (await import('@/features/admin/WeightsPage')).WeightsPage }) },
  { path: 'admin/outbox', lazy: async () => ({ Component: (await import('@/features/admin/OutboxPage')).OutboxPage }) },
  // перенаправлення — loader до рендеру (зберігає ?semester=…); element — запасний варіант без data-router
  {
    path: 'directories/scheduling_rules',
    element: <Navigate to="/admin/rules" replace />,
    lazy: async () => ({ loader: (await import('@/features/admin/AdminIndexRedirect')).rulesDirectoryLoader }),
  },
  {
    path: 'directories/weight_profiles',
    element: <Navigate to="/admin/weights" replace />,
    lazy: async () => ({ loader: (await import('@/features/admin/AdminIndexRedirect')).weightsDirectoryLoader }),
  },
  // </U16>
  // <U17>
  // </U17>
  { path: '*', element: <NotFoundPage /> },
];

const publicRoutes: RouteObject[] = [
  // <U14-public>
  { index: true, lazy: async () => ({ Component: (await import('@/features/publishing/public/PublicPages')).PublicHomePage }) },
  { path: 'faculty/:id', lazy: async () => ({ Component: (await import('@/features/publishing/public/PublicPages')).PublicFacultyPage }) },
  { path: 'group/:id', lazy: async () => ({ Component: (await import('@/features/publishing/public/PublicPages')).PublicGroupPage }) },
  { path: 'teacher/:id', lazy: async () => ({ Component: (await import('@/features/publishing/public/PublicPages')).PublicTeacherPage }) },
  { path: '*', lazy: async () => ({ Component: (await import('@/features/publishing/public/PublicPages')).PublicNotFoundPage }) },
  // </U14-public>
];

export const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <GuestOnly>
        <LoginPage />
      </GuestOnly>
    ),
    errorElement: <RouteError />,
  },
  {
    path: '/p',
    element: <PublicLayout />,
    errorElement: <RouteError />,
    hydrateFallbackElement: <LoadingBlock className="min-h-dvh" />,
    children: publicRoutes,
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <SemesterProvider>
          <AppShell />
        </SemesterProvider>
      </ProtectedRoute>
    ),
    errorElement: <RouteError />,
    // перший рендер із lazy-маршрутом модуля (прямий перехід на /directories/…): лоадер замість попередження React Router
    hydrateFallbackElement: <LoadingBlock className="min-h-dvh" />,
    children: shellRoutes,
  },
]);
